import { showError, showSuccess } from '@calendly/alert';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { CalendlyNotetaker } from '@calendly/ui/components/glyphs/CalendlyNotetaker';
import { IconDotsVertical } from '@calendly/ui/components/glyphs/IconDotsVertical';
import { IconDownload } from '@calendly/ui/components/glyphs/IconDownload';
import { IconLink } from '@calendly/ui/components/glyphs/IconLink';
import { IconShare3 } from '@calendly/ui/components/glyphs/IconShare3';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useMainContainerSize } from '@calendly/ui/hooks/main-container-size-context';
import { DeviceTypeProvider } from '@calendly/ui/hooks/use-device-type';
import { colorIconDestructive, colorTextDestructive } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeleteRecapsModal } from 'features/tracked_meetings';
import { rootApi } from 'root-api';
import { useRecapShare } from './recap_share_context';
import { isRecapOwner, isRecapViewer, RecapPermission } from '../../constants';
import { formatTranscriptContent, getFilenamePrefix } from '../../utils';
const generateFilename = trackedMeeting => {
  const prefix = getFilenamePrefix(trackedMeeting);
  if (!prefix) {
    return I18n.t('publisher.meeting_intelligence.transcript.filename');
  }
  return I18n.t('publisher.meeting_intelligence.transcript.filename_with_prefix', {
    prefix
  });
};

// Keep the kebab menu as an anchored popover at every width. CUI's DropdownMenu
// renders a fixed bottom sheet on phone-sized viewports, which is undesired here.
// We override the phone breakpoint so it never matches, which preserves the
// popover layout without affecting the rest of the card's responsiveness.
const ALWAYS_POPOVER_DEVICE_TYPE = {
  breakpoints: {
    isPhone: {
      maxWidth: 0
    }
  }
};
const actionsContainerCss = "a1z01u3r";
const ActionsContainer = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(actionsContainerCss, props.className)} />);
const RecapActionsMenu = ({
  permission,
  hasShareLink,
  onCopyLink,
  onDeleteRecap,
  onDownloadTranscript,
  onShareRecap,
  onViewRecap
}) => {
  const isOwner = isRecapOwner(permission);
  const isView = isRecapViewer(permission);
  const showDeleteGroup = isOwner;
  const showShare = !isView;
  const showCopyLink = showShare && hasShareLink;
  const handleViewRecap = event => {
    event?.preventDefault();
    event?.stopPropagation();
    onViewRecap(permission);
  };
  return <DropdownMenuContent data-testid="recap-actions-menu" placement="bottom end">
      <DropdownMenuGroup>
        <DropdownMenuItem onSelect={handleViewRecap}>
          <UiIcon icon={CalendlyNotetaker} size="small" title="" />
          {I18n.t('publisher.meeting_recap.recap_actions.view_recap')}
        </DropdownMenuItem>
        {showShare && <DropdownMenuItem onSelect={onShareRecap}>
            <UiIcon icon={IconShare3} size="small" title="" />
            {I18n.t('publisher.meeting_recap.recap_actions.share')}
          </DropdownMenuItem>}
        {showCopyLink && <DropdownMenuItem onSelect={onCopyLink}>
            <UiIcon icon={IconLink} size="small" title="" />
            {I18n.t('publisher.meeting_recap.recap_actions.copy_link')}
          </DropdownMenuItem>}
        <DropdownMenuItem onSelect={onDownloadTranscript}>
          <UiIcon icon={IconDownload} size="small" title="" />
          {I18n.t('publisher.meeting_recap.recap_actions.download_transcript')}
        </DropdownMenuItem>
      </DropdownMenuGroup>
      {showDeleteGroup && <DropdownMenuGroup>
          <DropdownMenuItem onSelect={onDeleteRecap}>
            <UiIcon icon={IconTrash} size="small" color={colorIconDestructive} title="" />
            <BodyText size="medium" color={colorTextDestructive}>
              {I18n.t('publisher.meeting_recap.recap_actions.delete')}
            </BodyText>
          </DropdownMenuItem>
        </DropdownMenuGroup>}
    </DropdownMenuContent>;
};
export const RecapActions = forwardRef(({
  onDeleteSuccess,
  onViewRecap
}, ref) => {
  const {
    isSmallMainContainer
  } = useMainContainerSize();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteRecapsModalOpen, openDeleteRecapsModal, closeDeleteRecapsModal] = useBooleanState(false);
  const dispatch = useDispatch();
  const {
    trackedMeeting: {
      uuid: trackedMeetingUuid
    },
    recapPermission: permission,
    shareLink: {
      url: shareLinkUrl,
      copy: handleCopyShareLink
    },
    shareModal: {
      open: openShareModal
    }
  } = useRecapShare();
  const showDeleteModal = permission !== RecapPermission.SHARE;
  const handleToggle = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDropdownOpen(open => !open);
  };
  const handleViewRecap = () => {
    setIsDropdownOpen(false);
    onViewRecap(permission);
  };
  const canShareRecap = !isRecapViewer(permission);
  const handleShareRecap = event => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsDropdownOpen(false);
    openShareModal();
  };
  const onCopyLink = () => {
    if (!shareLinkUrl) return;
    setIsDropdownOpen(false);
    handleCopyShareLink();
  };
  const handleDownloadTranscript = useCallback(async () => {
    if (!trackedMeetingUuid) return;
    setIsDropdownOpen(false);
    const trackedMeetingResult = await dispatch(rootApi.endpoints.fetchTrackedMeetingWithSummary.initiate({
      meetingUuid: trackedMeetingUuid
    }));
    if (!trackedMeetingResult || trackedMeetingResult.error || !trackedMeetingResult.data) {
      dispatch(showError(I18n.t('publisher.meeting_intelligence.transcript.fetching_error')));
      return;
    }
    const transcript = trackedMeetingResult.data.summaries?.transcript || [];
    const formattedContent = formatTranscriptContent(transcript);
    const filename = generateFilename(trackedMeetingResult.data);
    const blob = new Blob([formattedContent], {
      type: 'text/plain'
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = filename;
    link.href = downloadUrl;
    link.click();
    URL.revokeObjectURL(downloadUrl);
  }, [dispatch, trackedMeetingUuid]);
  const handleDeleteRecap = () => {
    setIsDropdownOpen(false);
    openDeleteRecapsModal();
  };
  const handleDeleteRecapSuccess = () => {
    dispatch(showSuccess(I18n.t('publisher.meeting_intelligence.details.delete_recap_modal.success')));
    onDeleteSuccess(trackedMeetingUuid);
  };
  return <ActionsContainer ref={ref}>
      {!isSmallMainContainer && canShareRecap && <IconButton aria-label={I18n.t('publisher.meeting_recap.recap_actions.share')} data-testid="recap-share-button" icon={IconShare3} onClick={handleShareRecap} size="small" title={I18n.t('publisher.meeting_recap.recap_actions.share')} />}
      <DeviceTypeProvider value={ALWAYS_POPOVER_DEVICE_TYPE}>
        <DropdownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
          <DropdownMenuTrigger>
            <IconButton aria-label={I18n.t('publisher.meetings.actions.more_actions')} aria-expanded={isDropdownOpen} icon={IconDotsVertical} onClick={handleToggle} size="small" title={I18n.t('publisher.meetings.actions.more_actions')} />
          </DropdownMenuTrigger>
          <RecapActionsMenu permission={permission} hasShareLink={Boolean(shareLinkUrl)} onCopyLink={onCopyLink} onDeleteRecap={handleDeleteRecap} onDownloadTranscript={handleDownloadTranscript} onShareRecap={handleShareRecap} onViewRecap={handleViewRecap} />
        </DropdownMenu>
      </DeviceTypeProvider>
      {showDeleteModal ? <DeleteRecapsModal isOpen={isDeleteRecapsModalOpen} meetingUuids={trackedMeetingUuid ? [trackedMeetingUuid] : []} onClose={closeDeleteRecapsModal} onSuccess={handleDeleteRecapSuccess} /> : null}
    </ActionsContainer>;
});

require("./recap_actions.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recap_actions.js");